import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import AuthWrapper from '../../components/AuthWrapper';
import AuthWrapperFooter from 'components/AuthWrapperFooter';
import BlockingSpinnerOverlay from 'components/BlockingSpinnerOverlay';
import EmailPhoneContactSelect from 'components/EmailPhoneContactSelect';
import identityClient from '../../clients/identityClient';
import store from 'store';
import FormatErrorMessage from '../FormatMessage/FormatErrorMessage';
import errorMessages from 'helpers/errorMessages';

const VerifyIdentity = () => {
    const location = useLocation();
    const history = useHistory();
    const { emails = [], phoneNumbers = [], verifyInfo, queryString } = location.state || {};
    const initialSelectedOption = phoneNumbers.length > 0
        ? phoneNumbers[0].value
        : (emails.length === 1 ? emails[0].value : null);
    const [selectedOption, setSelectedOption] = useState(initialSelectedOption);
    const [isLoading, setIsLoading] = useState(false);
    const [errorStatus, setErrorStatus] = useState('');

    const state = store.getState();
    const { appSettingVerificationCodeValidityPeriod } = state.auth.aws;

    useEffect(() => {
        if (!location.state) {
            history.replace('/login');
        }
    }, [location.state, history]);

    const handleSubmit = async () => {
        setIsLoading(true);
        const selectedEmail = emails.find((email) => email.value === selectedOption);
        const selectedPhone = phoneNumbers.find((phone) => phone.value === selectedOption);
        const email = selectedEmail ? selectedOption : '';
        const phone = selectedPhone ? selectedOption : '';
        const verificationMethod = selectedEmail ? 'email' : 'phone';
        const hstreamId = verifyInfo.hstreamId;

        try {
            await identityClient.SendOtp(hstreamId, email, phone, verificationMethod, queryString);
            history.push({
                pathname: '/confirmLogin',
                search: queryString,
                state: { username: selectedOption, queryString, hstreamId, emails, phoneNumbers, verifyInfo }
            });
        } catch (error) {
            if (error.response && error.response.data && error.response.data.status === 400 && error.response.data.title === "No Active Session") {
                setErrorStatus(errorMessages.noActiveSession.message);
            }
            else if (error.response && error.response.data) {
                setErrorStatus(error.response.data.detail);
            } else {
                setErrorStatus('We encountered an issue. You may try to sign in again, or if this error persists, please contact your administrator.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getValidationTimePeriod = () => {
        let period = appSettingVerificationCodeValidityPeriod;
        let validityMessage = "";
        if ((Math.floor(period / 60) == 1 && (period % 60) > 0) || Math.floor(period / 60) > 1) {
            if (Math.floor(period / 60) == 1) {
                validityMessage = "1 hour ";
            } else {
                validityMessage = Math.floor(period / 60) + " hours ";
            }
            period = (period % 60);
        }

        if (period > 1) {
            validityMessage = validityMessage + period + " minutes";
        } else if (period == 1) {
            validityMessage = validityMessage + "1 minute";
        }

        return validityMessage.trim();
    };

    const getExpirationMessage = () => {
        if (selectedOption) {
            const selectedEmail = emails.find((email) => email.value === selectedOption);
            if (selectedEmail) {
                return "The code expires in " + getValidationTimePeriod() + " after you request it. Please check your spam folder if you do not receive it.";
            }
        }
        return "The code expires in " + getValidationTimePeriod() + " after you request it.";
    };

    const headerContent = (
        <>
            <p style={{ fontSize: '16px' }}><b>Verify Your Identity</b></p>
            <hr />
            <p className='m-0 pb-1'><b>Request Authorization Code</b></p>
            <p>To verify your identity, we need to send an authorization code</p>
        </>
    );

    return (
        <>
            <AuthWrapper
                footer={<AuthWrapperFooter />}
            >
                {errorStatus && (
                    <FormatErrorMessage error={errorStatus} recoverErrorCode={undefined} recoverEmail={undefined} />
                )}
                <div
                    className="position-relative"
                    aria-busy={isLoading || undefined}
                >
                    <BlockingSpinnerOverlay
                        show={isLoading}
                        ariaLabel="Sending verification code, please wait"
                    />
                    <EmailPhoneContactSelect
                        selectionMode="single"
                        emails={emails}
                        phoneNumbers={phoneNumbers}
                        headerContent={headerContent}
                        emailSectionLabel="Select email address"
                        phoneSectionLabel="Select a phone number"
                        maskEmail
                        maskPhone
                        singleEmailReadOnly
                        selectedContact={selectedOption || ''}
                        onContactChange={setSelectedOption}
                        singleRadioGroupName="verificationOption"
                        footerNote={getExpirationMessage()}
                        primaryButtonId="verifyIdentityContinue"
                        onPrimaryClick={handleSubmit}
                        isLoading={isLoading}
                        buttonLayout="stack"
                    />
                </div>
            </AuthWrapper>
        </>
    );
};

export default VerifyIdentity;
